import { Modal, ModalHeader, ModalBody, Button, Icon } from '@evertrue/et-components';
import PropTypes from 'prop-types';

/**
 * Confirmation modal for disconnecting a Postmark DNS domain.
 * isOpen/closeModal are controlled by the parent to avoid unmount issues
 * when rendered alongside a popover-based dropdown.
 * activeCadenceCount and pendingBulkMessageCount are optional impact counts
 * shown to owners so they understand downstream effects before confirming.
 */
const DisconnectDnsModal = ({
  isOpen,
  closeModal,
  onConfirm,
  domainName,
  activeCadenceCount,
  pendingBulkMessageCount,
}) => {
  const handleConfirm = async () => {
    await onConfirm();
    closeModal();
  };

  const hasImpact = activeCadenceCount > 0 || pendingBulkMessageCount > 0;

  return (
    <Modal isOpen={isOpen} closeModal={closeModal} size="small">
      <ModalHeader title="Disconnect DNS Domain" closeModal={closeModal} />
      <ModalBody>
        <p>
          Are you sure you want to disconnect {domainName ? <strong>{domainName}</strong> : 'this domain'}? Your
          organization will no longer be able to send emails from this domain.
        </p>
        {hasImpact && (
          <div className="dns-disconnect-modal--impact-warning">
            <div className="dns-disconnect-modal--impact-title">
              <Icon icon="warning" size={1} />
              Active Automations Will Be Affected
            </div>
            <ul className="dns-disconnect-modal--impact-list">
              {activeCadenceCount > 0 && (
                <li>
                  <strong>
                    {activeCadenceCount} active cadence{activeCadenceCount !== 1 ? 's' : ''}
                  </strong>{' '}
                  will be automatically paused
                </li>
              )}
              {pendingBulkMessageCount > 0 && (
                <li>
                  <strong>
                    {pendingBulkMessageCount} scheduled bulk message{pendingBulkMessageCount !== 1 ? 's' : ''}
                  </strong>{' '}
                  will fail at send time
                </li>
              )}
            </ul>
          </div>
        )}
        {hasImpact && (
          <p className="dns-disconnect-modal--resume-note">
            These auto-paused cadences will resume automatically once a verified domain is reconnected — cadences
            you&apos;ve paused manually are not affected. Scheduled bulk messages that fail will need to be re-sent
            manually.
          </p>
        )}
      </ModalBody>
      <div className="dns-disconnect-modal--actions">
        <Button type="simple" onClick={closeModal}>
          Cancel
        </Button>
        <Button type="destroy" onClick={handleConfirm}>
          Disconnect DNS
        </Button>
      </div>
    </Modal>
  );
};

DisconnectDnsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  domainName: PropTypes.string,
  activeCadenceCount: PropTypes.number,
  pendingBulkMessageCount: PropTypes.number,
};

export default DisconnectDnsModal;
